import { useEffect, useRef, useState } from "react";

/**
 * Two-tier game timer.
 *  - per-question: counts UP from 0, capped at perQuestionLimit. On cap, calls onPerQuestionTimeout.
 *  - total: counts DOWN from totalLimit. On 0, calls onTotalTimeout.
 *
 * The timer continues even if the user backgrounds the tab — we use a ref to a
 * monotonic startedAt timestamp and recompute on visibility change.
 */
export interface UseGameTimerArgs {
  perQuestionLimitSec: number;
  totalLimitSec: number;
  onPerQuestionTimeout: () => void;
  onTotalTimeout: () => void;
  paused?: boolean;
  questionKey?: string | number; // change this to reset the per-question timer
}

export function useGameTimer({
  perQuestionLimitSec,
  totalLimitSec,
  onPerQuestionTimeout,
  onTotalTimeout,
  paused = false,
  questionKey,
}: UseGameTimerArgs) {
  const totalStartRef = useRef<number>(Date.now());
  const questionStartRef = useRef<number>(Date.now());
  const firedQuestionRef = useRef<boolean>(false);
  const firedTotalRef = useRef<boolean>(false);

  const [questionElapsedSec, setQuestionElapsedSec] = useState(0);
  const [totalRemainingSec, setTotalRemainingSec] = useState(totalLimitSec);

  // Reset per-question timer when questionKey changes
  useEffect(() => {
    questionStartRef.current = Date.now();
    firedQuestionRef.current = false;
    setQuestionElapsedSec(0);
  }, [questionKey]);

  useEffect(() => {
    if (paused) return;

    const tick = () => {
      const now = Date.now();
      const qElapsed = Math.floor((now - questionStartRef.current) / 1000);
      const tElapsed = Math.floor((now - totalStartRef.current) / 1000);
      setQuestionElapsedSec(qElapsed);
      setTotalRemainingSec(Math.max(0, totalLimitSec - tElapsed));

      if (!firedQuestionRef.current && qElapsed >= perQuestionLimitSec) {
        firedQuestionRef.current = true;
        onPerQuestionTimeout();
      }
      if (!firedTotalRef.current && tElapsed >= totalLimitSec) {
        firedTotalRef.current = true;
        onTotalTimeout();
      }
    };
    const id = setInterval(tick, 250);
    const onVis = () => tick();
    document.addEventListener("visibilitychange", onVis);
    tick();
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [paused, perQuestionLimitSec, totalLimitSec, onPerQuestionTimeout, onTotalTimeout]);

  return {
    questionElapsedSec,
    totalRemainingSec,
    questionRemainingSec: Math.max(0, perQuestionLimitSec - questionElapsedSec),
  };
}
